/**
 * Footer.js — Premium footer with gradient accent border and animated links
 */
import React from 'react';
import { Chrome } from 'lucide-react';
import { Button } from './ui';

// --- SVG Icons for Social Media ---
const InstagramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
);


export default function Footer({ navigate }) {
    const handleLinkClick = (e, path) => {
        e.preventDefault();
        navigate(path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const FooterLink = ({ href, onClick, children, external }) => (
        <li>
            <a
                href={href}
                onClick={onClick}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                className="text-slate-400 hover:text-white transition-colors duration-200 link-underline text-sm"
            >
                {children}
            </a>
        </li>
    );

    return (
        <footer className="bg-slate-900 text-white relative">
            {/* Gradient accent top border */}
            <div className="h-px bg-gradient-to-r from-transparent via-brand-500/50 to-transparent"></div>

            <div className="container mx-auto py-12 px-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
                    {/* Brand column */}
                    <div className="col-span-2 md:col-span-1">
                        <div className="flex items-center space-x-3 mb-4">
                            <img src="/images/logo_emblem.webp" alt="Gift Scout Emblem" className="h-10 w-10" width="40" height="40" loading="lazy" decoding="async" />
                            <span className="font-display text-lg font-bold">Gift Scout</span>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed mb-4">
                            Smart savings, simplified. Your trusted companion for gift card management.
                        </p>
                        <div className="flex space-x-3">
                            <a href="https://instagram.com/giftscoutapp" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-800" aria-label="Instagram">
                                <InstagramIcon />
                            </a>
                            <a href="https://x.com/usegiftscout" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-800" aria-label="X">
                                <XIcon />
                            </a>
                        </div>
                    </div>

                    {/* Navigate */}
                    <div>
                        <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-4">Navigate</h3>
                        <ul className="space-y-2.5">
                            <FooterLink href="/home" onClick={(e) => handleLinkClick(e, '/home')}>Home</FooterLink>
                            <FooterLink href="/tool" onClick={(e) => handleLinkClick(e, '/tool')}>Manager</FooterLink>
                            <FooterLink href="/deals" onClick={(e) => handleLinkClick(e, '/deals')}>Deals</FooterLink>
                            <FooterLink href="/extension" onClick={(e) => handleLinkClick(e, '/extension')}>Extension</FooterLink>
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-4">Company</h3>
                        <ul className="space-y-2.5">
                            <FooterLink href="/about" onClick={(e) => handleLinkClick(e, '/about')}>About Us</FooterLink>
                            <FooterLink href="/contact" onClick={(e) => handleLinkClick(e, '/contact')}>Contact</FooterLink>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-4">Legal</h3>
                        <ul className="space-y-2.5">
                            <FooterLink href="/terms" onClick={(e) => handleLinkClick(e, '/terms')}>Terms of Service</FooterLink>
                            <FooterLink href="/privacy" onClick={(e) => handleLinkClick(e, '/privacy')}>Privacy Policy</FooterLink>
                            <FooterLink href="/cookies" onClick={(e) => handleLinkClick(e, '/cookies')}>Cookie Policy</FooterLink>
                            <FooterLink href="/disclosure" onClick={(e) => handleLinkClick(e, '/disclosure')}>Disclosure</FooterLink>
                        </ul>
                    </div>

                    {/* CTA column */}
                    <div className="col-span-2 md:col-span-1">
                        <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-300 mb-4">Get the Extension</h3>
                        <p className="text-slate-400 text-sm mb-4">Auto-fill gift card codes at checkout with our free Chrome extension.</p>
                        <Button
                            as="a"
                            href="https://chrome.google.com/webstore"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outline"
                            size="sm"
                            icon={Chrome}
                            className="border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 hover:bg-slate-800"
                        >
                            Add to Chrome
                        </Button>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-10 pt-6 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center text-slate-500 text-xs">
                    <p>&copy; {new Date().getFullYear()} Gift Scout LLC. All rights reserved.</p>
                    <p className="mt-2 sm:mt-0">Made with 💚 for smart shoppers</p>
                </div>
            </div>
        </footer>
    );
}